import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  LinearProgress,
  Chip,
  Collapse,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  getSPDataWithParams,
  postSPFormDataWithoutParams,
} from 'src/supportPortal/services/services'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

const BASE = '/api/secure/cc/support/csm/patient/bulk-import'

// Each entry maps to an ImportSource on the backend; add new sources here as parsers are added.
const IMPORT_SOURCES = [{ value: 'EKA_CARE', label: 'EkaCare' }]

const TERMINAL_STATUSES = ['COMPLETED', 'COMPLETED_WITH_FAILURES', 'FAILED']
const ACTIVE_STATUSES = ['PENDING', 'RUNNING']

const OUTCOME_LABELS = {
  CREATED: 'Would create',
  SKIPPED_DUPLICATE: 'Duplicate (exists)',
  SKIPPED_IN_FILE_DUPLICATE: 'Duplicate in file',
  SKIPPED_ALREADY_IMPORTED: 'Already imported',
  FAILED: 'Invalid',
}

const STATUS_COLOR = {
  COMPLETED: 'success',
  COMPLETED_WITH_FAILURES: 'warning',
  FAILED: 'error',
  RUNNING: 'info',
  PENDING: 'default',
}

// The nginx edge caps the request body at 200 MB; warn a little under that so an oversized
// file gives a clear message instead of a 413 that the browser surfaces as a CORS error.
const MAX_UPLOAD_BYTES = 195 * 1024 * 1024
const humanSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`

function payload(resp, key) {
  return resp?.data?.data?.[key]
}

function isOk(resp) {
  return resp?.status === 200 && isCommonResponseSuccessful(resp?.data?.code)
}

export default function BulkImportPatientsDialog({ open, onClose, clinicBrandId, onImported }) {
  const [importSource, setImportSource] = useState('EKA_CARE')
  const [file, setFile] = useState(null)
  const [locations, setLocations] = useState([])
  const [clinicId, setClinicId] = useState('')
  const [linkExistingUsers, setLinkExistingUsers] = useState(true)
  const [createMissingUsers, setCreateMissingUsers] = useState(true)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const [loadingLocations, setLoadingLocations] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [preview, setPreview] = useState(null)
  const [starting, setStarting] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  const [jobs, setJobs] = useState([])
  const [loadingJobs, setLoadingJobs] = useState(false)

  const completedRef = useRef(new Set()) // job ids already counted as finished
  const initializedRef = useRef(false) // skip patient-refresh on the very first load

  // postSPFormDataWithoutParams resolves with the axios error object on failure, so the argument
  // here may be a CommonResponse-shaped object OR an axios error. Surface the real reason + status.
  const describeError = (respOrErr, fallback) => {
    const status = respOrErr?.response?.status ?? respOrErr?.status
    const apiMsg = respOrErr?.data?.message || respOrErr?.response?.data?.message
    // eslint-disable-next-line no-console
    console.error('Bulk import request failed:', { status, response: respOrErr })
    if (status === 404) {
      return 'Import API not found (HTTP 404) — the backend with bulk-import support is not deployed to this environment yet.'
    }
    if (apiMsg) return status ? `${apiMsg} (HTTP ${status})` : apiMsg
    if (status) return `${fallback} (HTTP ${status})`
    return fallback
  }

  const loadJobs = useCallback(async () => {
    if (!clinicBrandId) return
    setLoadingJobs(true)
    try {
      const resp = await getSPDataWithParams(`${BASE}/jobs`, { clinicBrandId, page: 1, pageSize: 15 })
      if (isOk(resp)) {
        const list = payload(resp, 'patientBulkImportJobs') || []
        setJobs(list)
        // Refresh the page's patient list once per job that newly finishes with creations
        // (skipped on the very first load so opening the dialog doesn't refetch needlessly).
        let refreshPatients = false
        list.forEach((job) => {
          if (TERMINAL_STATUSES.includes(job.status) && !completedRef.current.has(job.id)) {
            completedRef.current.add(job.id)
            if (initializedRef.current && (job.createdCount || 0) > 0) refreshPatients = true
          }
        })
        initializedRef.current = true
        if (refreshPatients) onImported?.()
      }
    } finally {
      setLoadingJobs(false)
    }
  }, [clinicBrandId, onImported])

  // Load locations + job history when the dialog opens.
  useEffect(() => {
    if (!open || !clinicBrandId) return
    let active = true
    setLoadingLocations(true)
    getSPDataWithParams(`${BASE}/clinic-locations`, { clinicBrandId })
      .then((resp) => {
        if (!active) return
        const locs = isOk(resp) ? payload(resp, 'clinicLocations') || [] : []
        setLocations(locs)
        if (locs.length === 1) setClinicId(locs[0].id)
      })
      .finally(() => active && setLoadingLocations(false))
    loadJobs()
    return () => {
      active = false
    }
  }, [open, clinicBrandId, loadJobs])

  // Poll the job log while the dialog is open and any job is still active.
  useEffect(() => {
    if (!open) return undefined
    const hasActive = jobs.some((j) => ACTIVE_STATUSES.includes(j.status))
    if (!hasActive) return undefined
    const id = setInterval(loadJobs, 3000)
    return () => clearInterval(id)
  }, [open, jobs, loadJobs])

  const buildFormData = (withOptions) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('importSource', importSource)
    fd.append('clinicBrandId', clinicBrandId)
    if (clinicId) fd.append('clinicId', clinicId)
    if (withOptions) {
      fd.append('linkExistingUsers', String(linkExistingUsers))
      fd.append('createMissingUsers', String(createMissingUsers))
    }
    return fd
  }

  const handlePreview = async () => {
    setError('')
    setNotice('')
    setPreviewing(true)
    try {
      const resp = await postSPFormDataWithoutParams(`${BASE}/preview`, buildFormData(false))
      if (isOk(resp)) {
        setPreview(payload(resp, 'bulkImportPreview'))
      } else {
        setError(describeError(resp, 'Could not preview the file.'))
      }
    } catch (e) {
      setError(describeError(e, 'Could not preview the file.'))
    } finally {
      setPreviewing(false)
    }
  }

  const handleStart = async () => {
    setError('')
    setNotice('')
    setStarting(true)
    try {
      const resp = await postSPFormDataWithoutParams(`${BASE}/upload`, buildFormData(true))
      if (isOk(resp)) {
        const job = payload(resp, 'patientBulkImportJob')
        setNotice(
          `Import started for "${job?.originalFilename || file?.name}" (${job?.totalRows ?? '?'} rows). ` +
            'It runs in the background — you can close this dialog; progress shows in the log below.',
        )
        setPreview(null)
        setFile(null)
        await loadJobs()
      } else {
        setError(describeError(resp, 'Could not start the import.'))
      }
    } catch (e) {
      setError(describeError(e, 'Could not start the import.'))
    } finally {
      setStarting(false)
    }
  }

  const handleClose = () => {
    // The poll interval is cleared by its effect cleanup when `open` flips to false.
    setError('')
    setNotice('')
    setPreview(null)
    onClose?.()
  }

  const canSubmit = file && clinicId && importSource && !starting && !previewing

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Bulk Import Patients
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
            {notice}
          </Alert>
        )}

        {/* ── New import ─────────────────────────────────────────────── */}
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          New import
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Import source"
              value={importSource}
              onChange={(e) => setImportSource(e.target.value)}
              sx={{ minWidth: 200, flex: 1 }}
            >
              {IMPORT_SOURCES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Target clinic location"
              value={clinicId}
              onChange={(e) => setClinicId(e.target.value)}
              sx={{ minWidth: 240, flex: 1 }}
              helperText={
                loadingLocations
                  ? 'Loading locations…'
                  : locations.length === 0
                    ? 'No clinic locations found for this brand'
                    : 'Imported patients are assigned to this location'
              }
            >
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.nickName || loc.title || loc.id}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
              {file ? file.name : 'Choose CSV / XLSX file'}
              <input
                type="file"
                hidden
                accept=".csv,.xlsx,.xls"
                onChange={(e) => {
                  const picked = e.target.files?.[0] || null
                  if (picked && picked.size > MAX_UPLOAD_BYTES) {
                    setError(
                      `"${picked.name}" is ${humanSize(picked.size)}, over the ${humanSize(MAX_UPLOAD_BYTES)} upload limit. ` +
                        'Split it into smaller batches and import them separately.',
                    )
                    setFile(null)
                    setPreview(null)
                    e.target.value = ''
                    return
                  }
                  setError('')
                  setFile(picked)
                  setPreview(null)
                }}
              />
            </Button>

            <Button variant="contained" onClick={handleStart} disabled={!canSubmit}>
              {starting ? <CircularProgress size={20} /> : 'Start Import'}
            </Button>

            <Tooltip title="Optional dry run — classifies rows without creating anything">
              <span>
                <Button variant="text" onClick={handlePreview} disabled={!canSubmit}>
                  {previewing ? <CircularProgress size={18} /> : 'Preview (optional)'}
                </Button>
              </span>
            </Tooltip>

            <Button size="small" onClick={() => setShowAdvanced((v) => !v)} sx={{ ml: 'auto' }}>
              {showAdvanced ? 'Hide options' : 'Account options'}
            </Button>
          </Box>

          <Collapse in={showAdvanced}>
            <Box sx={{ pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={linkExistingUsers}
                    onChange={(e) => setLinkExistingUsers(e.target.checked)}
                  />
                }
                label="Link to an existing user when the mobile matches"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createMissingUsers}
                    onChange={(e) => setCreateMissingUsers(e.target.checked)}
                  />
                }
                label="Create an account for new mobiles (welcome messages suppressed)"
              />
            </Box>
          </Collapse>

          {/* Optional preview result */}
          <Collapse in={Boolean(preview)}>
            {preview && (
              <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                {!preview.brandKycApproved && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    This clinic brand&apos;s KYC is not approved yet — the import will be rejected
                    until it is.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip size="small" label={`${preview.totalRows} rows`} />
                  <Chip size="small" color="success" label={`${preview.wouldCreate} new`} />
                  <Chip size="small" label={`${preview.wouldSkipInFileDuplicate} in-file dup`} />
                  {preview.invalidRows > 0 && (
                    <Chip size="small" color="error" label={`${preview.invalidRows} invalid`} />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Preview validates parsing + in-file duplicates only. De-duplication against existing
                  patients runs during the import and appears in the log below.
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Mobile</TableCell>
                      <TableCell>UHID</TableCell>
                      <TableCell>Outcome</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(preview.sample || []).map((r) => (
                      <TableRow key={r.rowNumber}>
                        <TableCell>{r.rowNumber}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.mobile || '—'}</TableCell>
                        <TableCell>{r.uhId || '—'}</TableCell>
                        <TableCell>{OUTCOME_LABELS[r.classification] || r.classification}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Collapse>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* ── Import log ─────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">Recent imports</Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadJobs} disabled={loadingJobs}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {jobs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {loadingJobs ? 'Loading…' : 'No imports yet for this brand.'}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell align="right">Created</TableCell>
                <TableCell align="right">Linked</TableCell>
                <TableCell align="right">Skipped</TableCell>
                <TableCell align="right">Failed</TableCell>
                <TableCell>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => {
                const skipped =
                  (job.skippedDuplicateCount || 0) +
                  (job.skippedInFileDuplicateCount || 0) +
                  (job.skippedAlreadyImportedCount || 0)
                const pct =
                  job.totalRows > 0 ? Math.round((job.processedRows / job.totalRows) * 100) : 0
                return (
                  <TableRow key={job.id}>
                    <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Tooltip title={job.originalFilename || ''}>
                        <span>{job.originalFilename || job.id}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLOR[job.status] || 'default'} label={job.status} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {ACTIVE_STATUSES.includes(job.status) ? (
                        <Box>
                          <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            {job.processedRows}/{job.totalRows}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {job.processedRows}/{job.totalRows}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{job.createdCount || 0}</TableCell>
                    <TableCell align="right">
                      {(job.linkedToUserCount || 0) + (job.createdUserCount || 0)}
                    </TableCell>
                    <TableCell align="right">{skipped}</TableCell>
                    <TableCell align="right">{job.failedCount || 0}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {job.createdTime ? `${job.createdTime} UTC` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
